import _defineProperty from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
var __jsx = React.createElement;
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useState, useMemo, useCallback, useEffect } from "react";
import FormSection from "./FormSection";
import { useFormSectionContext } from "./FormSectionContext";
import classNames from "classnames";
import { calculateRequiredFieldsStatus } from "../../../utils/acrUtils";
import { useFieldGrouping } from "../hooks/useFieldGrouping";
var GroupedFormSection = function GroupedFormSection(_ref) {
  var searchQuery = _ref.searchQuery,
    onExpandSectionReady = _ref.onExpandSectionReady;
  // Get data from context
  var _useFormSectionContex = useFormSectionContext(),
    filteredFields = _useFormSectionContex.filteredFields,
    fieldErrorMap = _useFormSectionContex.fieldErrorMap,
    formData = _useFormSectionContex.formData,
    isDropdownOpen = _useFormSectionContex.isDropdownOpen,
    submissionDetail = _useFormSectionContex.submissionDetail;

  // Generate field grouping dynamically from section and sub_section in fieldSchemas
  var fieldGroupingData = useFieldGrouping(filteredFields);
  var _useState = useState(new Set(fieldGroupingData.map(function (group) {
      return group.id;
    }))),
    expandedSections = _useState[0],
    setExpandedSections = _useState[1];

  // Toggle section expand/collapse (memoized)
  var toggleSection = useCallback(function (sectionId) {
    setExpandedSections(function (prev) {
      var newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet["delete"](sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  }, []);

  // Expand section (ensure it's opened, memoized)
  var expandSection = useCallback(function (sectionId) {
    setExpandedSections(function (prev) {
      if (prev.has(sectionId)) {
        return prev; // Already expanded, no change
      }
      var newSet = new Set(prev);
      newSet.add(sectionId);
      return newSet;
    });
  }, []);

  // Check if section is expanded (memoized)
  var isSectionExpanded = useCallback(function (sectionId) {
    return expandedSections.has(sectionId);
  }, [expandedSections]);

  // Notify parent when section API is ready
  useEffect(function () {
    if (onExpandSectionReady) {
      onExpandSectionReady({
        expand: expandSection,
        isExpanded: isSectionExpanded
      });
    }
  }, [expandSection, isSectionExpanded, onExpandSectionReady]);

  // Get field schema by field name (memoized)
  var getFieldSchema = useCallback(function (fieldName) {
    return filteredFields.find(function (f) {
      return f.field_name === fieldName;
    });
  }, [filteredFields]);

  // Count required fields in a section (memoized) - using centralized function
  var getRequiredFieldsCount = useCallback(function (group) {
    var allFields = group.sub_sections.flatMap(function (sub) {
      return sub.fields;
    });

    // Get field schemas for this group
    var groupFieldSchemas = allFields.map(function (fieldName) {
      return getFieldSchema(fieldName);
    }).filter(function (schema) {
      return schema !== undefined;
    });

    // Build conditional errors from fieldErrorMap
    var conditionalErrors = groupFieldSchemas.map(function (schema) {
      var fieldError = fieldErrorMap.get(schema.field_name);
      if (!(fieldError !== null && fieldError !== void 0 && fieldError.conditional_metadata)) return null;
      return {
        field: schema.field_name,
        type: "conditional",
        conditional_metadata: fieldError.conditional_metadata
      };
    }).filter(function (err) {
      return err !== null;
    });

    // Use centralized calculation function
    // Pass missing_fields to include server-side required fields in the count
    var status = calculateRequiredFieldsStatus(groupFieldSchemas, formData, conditionalErrors, submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.missing_fields);
    return {
      filled: status.filled,
      total: status.totalRequired
    };
  }, [getFieldSchema, fieldErrorMap, formData, submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.missing_fields]);

  // Get visible groups based on search query (memoized)
  var visibleGroups = useMemo(function () {
    if (!searchQuery) {
      return fieldGroupingData;
    }
    return fieldGroupingData.filter(function (group) {
      var allFields = group.sub_sections.flatMap(function (sub) {
        return sub.fields;
      });
      return allFields.some(function (fieldName) {
        return filteredFields.find(function (f) {
          return f.field_name === fieldName;
        }) !== undefined;
      });
    });
  }, [fieldGroupingData, searchQuery, filteredFields]);

  // Get visible subsections with fields for each group (memoized)
  var groupsWithVisibleSubSections = useMemo(function () {
    return visibleGroups.map(function (group) {
      var visibleSubSections = group.sub_sections.map(function (subSection) {
        if (!searchQuery) {
          return subSection;
        }
        // Check if subsection has any visible fields
        var hasVisibleFields = subSection.fields.some(function (fieldName) {
          return filteredFields.find(function (f) {
            return f.field_name === fieldName;
          }) !== undefined;
        });
        return hasVisibleFields ? subSection : null;
      }).filter(function (sub) {
        return sub !== null;
      });
      return _objectSpread(_objectSpread({}, group), {}, {
        visibleSubSections: visibleSubSections
      });
    });
  }, [visibleGroups, searchQuery, filteredFields]);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames("pl-5 pr-3 h-full overflow-y-auto scrollbar-light dark:scrollbar", {
      "pb-6": !isDropdownOpen,
      "pb-66": isDropdownOpen
    })
  }, /*#__PURE__*/React.createElement("div", {
    className: "py-4"
  }, groupsWithVisibleSubSections.map(function (group) {
    var isExpanded = expandedSections.has(group.id);
    var _getRequiredFieldsCou = getRequiredFieldsCount(group),
      filled = _getRequiredFieldsCou.filled,
      total = _getRequiredFieldsCou.total;
    return /*#__PURE__*/React.createElement(FormSection, {
      key: group.id,
      group: group,
      isExpanded: isExpanded,
      filled: filled,
      total: total,
      onToggle: toggleSection
    });
  })));
};
export default GroupedFormSection;